import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Monitor, Smartphone, Calendar } from "lucide-react";

interface SEOPreviewProps {
  title: string;
  description: string;
  slug: string;
  className?: string;
}

export function SEOPreview({
  title,
  description,
  slug,
  className,
}: SEOPreviewProps) {
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");

  const displayTitle = title
    ? `${title.slice(0, 60)}${title.length > 60 ? "..." : ""} | Aceena Blog`
    : "Título do Artigo | Aceena Blog";

  const displayDescription = description
    ? `${description.slice(0, 160)}${description.length > 160 ? "..." : ""}`
    : "Descrição do artigo aparecerá aqui. Use até 160 caracteres para melhor visualização nos resultados de busca.";

  const displayUrl = slug
    ? `aceena.com.br › blog › ${slug}`
    : "aceena.com.br › blog › seu-artigo";

  const today = new Date().toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  // Quality indicators
  const titleLength = title?.length || 0;
  const descLength = description?.length || 0;

  const getTitleQuality = () => {
    if (titleLength === 0) return { color: "bg-muted", label: "Vazio" };
    if (titleLength > 60) return { color: "bg-red-500", label: "Muito longo" };
    if (titleLength >= 50) return { color: "bg-green-500", label: "Ideal" };
    if (titleLength >= 30) return { color: "bg-amber-500", label: "Curto" };
    return { color: "bg-red-500", label: "Muito curto" };
  };

  const getDescQuality = () => {
    if (descLength === 0) return { color: "bg-muted", label: "Vazio" };
    if (descLength > 160) return { color: "bg-red-500", label: "Muito longo" };
    if (descLength >= 140) return { color: "bg-green-500", label: "Ideal" };
    if (descLength >= 80) return { color: "bg-amber-500", label: "Curto" };
    return { color: "bg-red-500", label: "Muito curto" };
  };

  const titleQuality = getTitleQuality();
  const descQuality = getDescQuality();

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">
          Preview do Google
        </p>
        <div className="flex items-center gap-1 border rounded-md p-0.5">
          <Button
            variant={viewMode === "desktop" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setViewMode("desktop")}
          >
            <Monitor className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={viewMode === "mobile" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setViewMode("mobile")}
          >
            <Smartphone className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Quality Indicators */}
      <div className="flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <div className={cn("w-2 h-2 rounded-full", titleQuality.color)} />
          <span className="text-muted-foreground">
            Título: <span className="text-foreground">{titleQuality.label}</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={cn("w-2 h-2 rounded-full", descQuality.color)} />
          <span className="text-muted-foreground">
            Descrição: <span className="text-foreground">{descQuality.label}</span>
          </span>
        </div>
      </div>

      {/* Preview Card */}
      <div
        className={cn(
          "bg-background rounded-lg border border-border overflow-hidden transition-all",
          viewMode === "mobile" ? "max-w-[320px]" : "w-full"
        )}
      >
        <div className="p-4 space-y-1">
          {/* Breadcrumb URL */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <img
              src="/favicon.ico"
              alt=""
              className="w-4 h-4 rounded-sm"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            <span className="truncate">{displayUrl}</span>
          </div>

          {/* Title */}
          <p
            className={cn(
              "text-primary hover:underline cursor-pointer font-medium leading-snug",
              viewMode === "mobile" ? "text-base" : "text-lg"
            )}
          >
            {displayTitle}
          </p>

          {/* Rich Snippet - Date */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{today}</span>
          </div>

          {/* Description */}
          <p
            className={cn(
              "text-muted-foreground",
              viewMode === "mobile" ? "text-xs line-clamp-3" : "text-sm line-clamp-2"
            )}
          >
            {displayDescription}
          </p>
        </div>
      </div>

      {/* Tips */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p>• Título ideal: 50-60 caracteres com palavra-chave no início</p>
        <p>• Descrição ideal: 140-160 caracteres com CTA implícito</p>
      </div>
    </div>
  );
}
